export const ADYEN_CARD_STYLES: Record<string, unknown> = {
  base: {
    color: '#000000',
    fontSize: '16px',
    fontSmoothing: 'antialiased',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Open Sans, Helvetica Neue, sans-serif !important',
    lineHeight: '1.5',
    outline: 'none !important',
    border: 'none',
    padding: '0 8px',
  },
  error: {
    color: '#ef4444',
    outline: 'none',
    border: 'none',
  },
  validated: {
    color: '#000000',
    border: 'none',
    outline: 'none',
  },
  placeholder: {
    color: '#9ca3af',
    fontWeight: '400',
    border: 'none',
    outline: 'none',
  },
  focus: {
    color: '#000000',
    border: 'none',
    outline: 'none',
  },
};

/**
 * Shared, template-agnostic shell classes. Always applied as the first
 * argument to `cn(...)`, with `template.styles.X` (or a `TEMPLATE_CLASSES`
 * token) layered on top.
 */
export const DETAILS_STYLES = {
  base: 'flex flex-col sm:w-full',
  // Landscape fill: in the two-column hero (DEFAULT/PREFERRED) the details fill
  // the 100dvh column and center their content vertically (with overflow scroll
  // for long breakdowns). The single-column SINGLE view opts OUT — there the
  // details must be content-height so the hero band stays short and the payment
  // sits directly below instead of being pushed off-screen.
  //
  // Centering uses auto margins on the first/last child rather than
  // `justify-center`: on a scroll container `justify-center` centers the content
  // AND clips both ends when it's taller than the column — and the clipped top
  // (and bottom, e.g. the Total) can't be scrolled into view. Auto margins
  // center when there's spare height but collapse to 0 when content overflows,
  // so a short landscape viewport scrolls the full breakdown from the top
  // instead of cutting it off. Mirrors the `my-auto` pattern in
  // PreferredPaymentView's payment column.
  //
  // GATE LIVES IN JSX (not in a `landscape:` prefix): applied conditionally by
  // `Details.tsx` via `useIsLandscape()` so the fill matches the app's
  // phone-landscape band (`700 ≤ w ≤ 1023 && h ≤ 500 && w > h` — see
  // `isLandscape` in `utils/device`) rather than Tailwind's looser
  // `@media (orientation: landscape)` (which fires on every `w > h`, incl.
  // desktop and dev-tools states like 714×547). Single source of truth for the
  // band lives in `isLandscape`.
  landscapeFill: 'h-[100dvh] overflow-y-auto [&>*:first-child]:mt-auto [&>*:last-child]:mb-auto',
  // Scroll shell + top-gap for CSS-landscape-but-JS-portrait viewports (e.g.
  // the dev-tools 714×547 handle, tablet in landscape 1024×768, desktop in
  // landscape). Without this the column is content-height with no overflow
  // container, so expanding the OffersPane / opening the items list pushes
  // Total off-screen with no way to scroll to it. Also compensates for the
  // template's `landscape:mt-0` on `detailsContainer` — uses `pt-15` (padding)
  // instead of `mt-15` (margin) so the 60px sits INSIDE the 100dvh column and
  // doesn't stretch the page.
  //
  // vs `landscapeFill` above: no auto-margin centering. Content anchors at
  // the top of the padding block; when it overflows the column, it scrolls.
  landscapeScroll: 'landscape:h-[100dvh] landscape:overflow-y-auto landscape:pt-15',
  // Grid-rows collapse shell. Animating `grid-template-rows` between `1fr`
  // and `0fr` collapses the ACTUAL content height as one smooth unit — no
  // `max-height` "dead zone" (where a too-large ceiling delays then rushes
  // the motion) and no bottom-up clipping that makes rows vanish one by one.
  collapsibleBase: 'grid w-full transition-all duration-300 ease-in-out',
} as const;

export const PAYMENT_STYLES = {
  base: 'flex flex-col h-full',
} as const;

/**
 * Named visual tokens used by the template style table
 * (`TemplateStyleConfigurations` in `templateConfig.ts`) AND by leaf
 * components. Each constant is named after *what it does*, not what
 * Tailwind classes it emits. This is the only place template Tailwind
 * soup lives — everything else just references these names.
 *
 * Groups (read top→bottom):
 *   1. Details section variants     — outer + wrapper for the items/price column
 *   2. Collapsible animation states — used by the purchased-items collapsible
 *   3. Payment section variants     — outer wrapper for the right-hand column
 *   4. Payment column composition   — inner stack + scroll/clamp modifiers
 *   5. Misc modifiers               — small ad-hoc toggles
 */
export const TEMPLATE_CLASSES = {
  detailsContainerDefault:
    'mt-4 pl-4 pr-4 w-full md:mt-15 md:pl-0 md:pr-0 md:items-center landscape:mt-0 landscape:items-center landscape:pl-5 landscape:pr-5',
  detailsContainerCompact: 'mt-0 px-0 w-full text-card-foreground landscape:items-center landscape:pl-5 landscape:pr-5',
  detailsWrapperDesktopHalfColumn: 'flex flex-col md:w-5/6',
  detailsWrapperCompactColumn: 'flex flex-col gap-1 w-full',
  // Inner products + price-breakdown stack inside DetailsWrapper.
  // Default keeps the legacy rhythm; Spaced adds an explicit row gap
  // so the Subtotal row doesn't sit flush against the products list
  // (visually crowded in PREFERRED + SINGLE).
  detailsBodyStackDefault: 'flex flex-col w-full',
  detailsBodyStackSpaced: 'flex flex-col w-full gap-3',

  // Outer grid-rows states (paired with `DETAILS_STYLES.collapsibleBase`).
  collapsibleClosed: 'grid-rows-[0fr] opacity-0',
  collapsibleOpen: 'grid-rows-[1fr] opacity-100',
  // Inner content wrapper — ALWAYS applied (not gated on open/closed) so the
  // content's intrinsic height stays constant while the grid row collapses.
  // `overflow-hidden min-h-0` lets the `1fr→0fr` row actually clip the content.
  collapsibleContentStandard: 'overflow-hidden min-h-0 flex flex-col gap-4',
  collapsibleContentTight: 'overflow-hidden min-h-0 flex flex-col gap-1',

  // NOTE: the section-to-footer gap is the canonical `gap-4` (16px). It used to
  // be `gap-10` (40px), but `justify-between` already pins the Footer to the
  // bottom when content is short (so the larger gap was invisible there) while
  // forcing an extra 24px of MINIMUM height when content is near the viewport —
  // that surplus tipped portrait DEFAULT into an unnecessary scrollbar (content
  // overflowed by a few px even though it visually "fit"). 16px removes the
  // surplus without changing the short-content layout.
  paymentContainerDefault:
    'mx-4 md:mx-0 gap-4 md:items-center landscape:items-center landscape:justify-start landscape:mx-0 landscape:px-4 landscape:pt-14 landscape:pb-4 landscape:gap-3 justify-between',
  paymentContainerCompact: 'mt-4 px-0 w-full gap-2 justify-between h-full',
  /**
   * Express-first single-CTA stack. Used by PREFERRED on mobile portrait
   * and (via `isExpressFirstMobile` widening to landscape) on the
   * landscape phone branch too — the landscape view wraps this section
   * with its own centering container, so no landscape:* tokens are
   * needed here.
   */
  paymentContainerExpressFirst: 'h-auto w-full gap-[18px] justify-between gap-10',

  /** The flex stack of [express, methods, form, viewer, error] inside the payment column.
   *  `gap-4` (16px) is the canonical section rhythm — every payment-body boundary
   *  (express ↔ tiles ↔ card form) sits on the same 16px grid as the Figma spec. */
  paymentInnerStack: 'flex flex-col gap-4 flex-1 min-h-0 md:max-w-90 md:w-full md:my-0',
  /**
   * MOBILE-ONLY content sizing for the DEFAULT inner stack (portrait phones).
   *
   * `min-h-fit` sizes the stack to its content so tall payment content (e.g. an
   * APM with a card/tax form) overflows up to the mobile scroll container
   * (`checkout-container` is `h-full overflow-y-auto`) and pushes the Footer
   * BELOW it — without it the `flex-1` stack is clamped to the bounded section
   * and tall content overlaps the Footer.
   *
   * `md:min-h-0` resets this on desktop/tablet (>=md): there the SECTION grows
   * and the column scrolls (`paymentColumnDesktopGrow`), and `min-height:
   * fit-content` must NOT be present because Safari ignores it on flex children
   * — that's the exact bug that broke desktop scrolling.
   */
  paymentInnerStackMobileContentFit: 'min-h-fit md:min-h-0',
  /**
   * Desktop payment SECTION sizing — a growable wrapper, NOT its own scroller.
   *
   * The single scroll viewport on desktop is the parent column
   * (DEFAULT: the `md:h-full md:overflow-y-auto` column in `DefaultView`;
   * SINGLE: the `flex-1 overflow-y-auto` wrapper in `OnePaymentView`). The
   * section must therefore:
   *   - `md:h-auto`     — drop the base `h-full` so the section can grow past
   *                       the column when its content is tall (otherwise it's
   *                       pinned to the column height and never overflows → the
   *                       parent never scrolls).
   *   - `md:min-h-full` — but still fill the column when content is SHORT, so
   *                       the `flex-1` inner stack can push the Footer to the
   *                       bottom (footer pinned when short, scrolls into view
   *                       when tall).
   *   - `md:shrink-0`   — stop the flex parent from shrinking the section back
   *                       down to the column height (which would re-clip the
   *                       Pay button and kill the overflow).
   *
   * Deliberately NO `overflow` here: a second overflow container is exactly the
   * double-scrollbar regression. And NO `min-height: fit-content` anywhere —
   * Safari ignores it for flex children, which is what broke scrolling before.
   * This is pure percentage height + flex-grow, which every browser honors, so
   * the whole column scrolls as ONE scrollbar and the Pay button is reachable.
   */
  paymentColumnDesktopGrow: 'md:h-auto md:min-h-full md:shrink-0',
  /**
   * Landscape phone band (700–1023px) equivalent of {@link paymentColumnDesktopGrow}.
   *
   * The landscape band straddles the `md` breakpoint (768px), so the `md:`
   * tokens above only cover its upper half — we mirror them with `landscape:`
   * tokens so the WHOLE band uses the single-scroll-column model. This makes
   * the parent column (`landscape:overflow-y-auto` in DefaultView) the one and
   * only scroll viewport, so the Footer scrolls together with the content
   * instead of being pinned by an inner-stack scroll. `h-auto` lets the section
   * grow past the column when content is tall; `min-h-full` still fills it when
   * short (footer pinned to the bottom); `shrink-0` stops flex from clamping it
   * back to the column height (which would re-introduce the inner scroll).
   */
  paymentColumnLandscapeGrow: 'landscape:h-auto landscape:min-h-full landscape:shrink-0',
  /** Express-first mobile: collapses the inner stack to a single button-sized row. */
  paymentInnerStackExpressFirstMobile: 'w-full flex-none',
  /**
   * `footerFollowsContent` templates (EMBEDDED_VIEW): the payment SECTION hugs its content
   * instead of filling the column.
   *
   * `h-auto` drops `PAYMENT_STYLES.base`'s `h-full` and `justify-start`
   * replaces the template's `justify-between` — together they stop the section
   * from spreading its two children (inner stack, Footer) to opposite edges,
   * which is what opened the dead space between the Pay button and the Footer.
   * Relies on `cn`'s tailwind-merge, so this must be layered AFTER
   * `template.styles.paymentContainer`.
   */
  paymentColumnContentHeight: 'h-auto justify-start',
  /**
   * Pair with {@link paymentColumnContentHeight}: `paymentInnerStack` sets
   * `flex-1`, which would grow the stack into the leftover column height and
   * re-open the gap the section no longer creates. `flex-none` sizes the stack
   * to its content so the Footer lands directly beneath the form.
   */
  paymentInnerStackContentHeight: 'flex-none',
  /**
   * Footer pinned to the bottom edge of the scroll viewport while the form
   * scrolls behind it. Only takes effect once the column actually overflows —
   * with short content the Footer simply sits in flow right under the form.
   *
   * `bg-white` is required: a transparent sticky Footer lets the scrolling form
   * show through it. `z-10` keeps it above the form for the same reason, but
   * stays well below the payment-in-flight interaction blocker.
   */
  footerWrapperSticky: 'sticky bottom-0 z-10 bg-white',
  /**
   * `errorFollowsContent` templates (EMBEDDED_VIEW): the error banner as an
   * overlay hanging off the TOP edge of the Footer wrapper (`bottom-full`), so
   * it floats over the end of the form instead of taking a row — the Footer and
   * everything above it stay exactly where they are, and the height reported to
   * the embedding SDK doesn't move either.
   *
   * `px-4` re-insets it to the payment column: the mobile Footer wrapper is
   * stretched edge-to-edge (`footerWrapperEdgeToEdgeMobile`), so a `w-full`
   * child would otherwise run to the screen edges. `pb-3` is the gap it keeps
   * above the Footer, `z-10` keeps it over the form it covers.
   */
  errorOverlayAboveFooter: 'absolute bottom-full left-0 z-10 w-full px-4 pb-3',
  /**
   * Bumps the inner stack down to align with the details column on desktop.
   * Forced back to zero in landscape — the 450px-tall phone landscape band
   * can't afford 60px of dead top space (it would push the card form below
   * the visible column).
   */
  paymentInnerStackDesktopTopOffset: 'md:mt-15 landscape:mt-0',
  /** Disable pointer events while a payment is in flight (prevents double-submit). */
  paymentInnerStackDisabled: 'pointer-events-none',
  /**
   * Landscape inner stack: cap the express buttons / method tabs / card
   * form at the same `max-w-90` the desktop column uses and center them in
   * the 3/5 payment column. The landscape phone band (700–1023px) straddles
   * the `md` breakpoint (768px), so the inherited `md:max-w-90` only applies
   * on the upper half of the band — we set an explicit `landscape:max-w-90`
   * so the width is consistent across the whole band (otherwise the buttons
   * stretch full-bleed and read as oversized, especially on wide landscape).
   */
  paymentInnerStackLandscape: 'landscape:w-full landscape:max-w-90 landscape:mx-auto landscape:my-0',
  /** Footer wrapper sits at the bottom and never shrinks. */
  footerWrapper: 'w-full shrink-0',
  /**
   * On mobile, the payment column is inset from the card edge by horizontal
   * padding/margins (SINGLE via OnePaymentView's `px-4` wrapper, DEFAULT via
   * `paymentContainerDefault`'s `mx-4`), which leaves the footer divider short
   * of the card edges. Since the card is full-width on mobile, stretch the
   * footer to the full viewport width (`100vw`, recentered) so the divider runs
   * from one card edge to the other. Scoped to `max-md` — desktop/landscape
   * keep the centered column footer untouched.
   */
  footerWrapperEdgeToEdgeMobile: 'max-md:w-screen max-md:relative max-md:left-1/2 max-md:-translate-x-1/2',

  paymentContainerEmbeddedView:
    'mx-0 gap-4 md:items-center landscape:items-center landscape:justify-start landscape:mx-0 landscape:px-4 landscape:pt-14 landscape:pb-4 landscape:gap-3 justify-between',
} as const;
